// services/exerciseService.js
import axios from 'axios';
import { logoutUser } from './dataService';

const API_URL = process.env.REACT_APP_API_URL || 'https://apienglish.nguyenluan.vn/api';

/**
 * A centralized error handler for Exercise API calls.
 * It checks for 401 Unauthorized errors and logs the user out.
 */
const handleApiError = (error, defaultMessage) => {
    console.error(`Exercise API Error:`, error.response ? error.response.data : error.message);
    if (error.response && error.response.status === 401) {
        logoutUser(); // This will remove token and reload the page
        throw new Error('Token không hợp lệ hoặc đã hết hạn. Đang đăng xuất...');
    }
    const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || defaultMessage;
    throw new Error(errorMessage);
};

/**
 * Gets the authorization header with the JWT token.
 * If no token is found, it logs the user out.
 */
const getAuthConfig = (token) => {
    if (!token) {
        console.error('Lỗi xác thực: Không tìm thấy token.');
        logoutUser(); // Force logout and reload if token is missing
        throw new Error('Yêu cầu xác thực. Vui lòng đăng nhập lại.');
    }
    return {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    };
};

// 🚀 Generate multiple exercises (OPTIMIZED)
const generateExerciseBatch = async (topic, difficulty = 'medium', count = 5, token) => {
    try {
        const config = getAuthConfig(token);
        
        // Dynamic timeout based on number of questions
        // 3 câu: 60s, 5 câu: 120s, 10 câu: 240s, 15 câu: 360s, 20 câu: 480s
        const timeoutMs = Math.max(60000, count * 24000); // 24 seconds per question minimum
        
        const response = await axios.post(`${API_URL}/exercises/generate-batch`,
            { topic, difficulty, count },
            {
                ...config,
                timeout: timeoutMs
            }
        );

        // Enhanced response handling
        const { data } = response;

        return {
            success: data.success,
            exercises: data.data || [],
            message: data.message,
            meta: data.meta || {
                topic,
                difficulty,
                requested: count,
                generated: data.data?.length || 0
            }
        };
    } catch (error) {
        // Enhanced error handling with timeout specific messages
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
            const estimatedTime = Math.max(1, Math.ceil(count * 24000/60000)); // Recalculate timeout estimate
            throw {
                status: 'TIMEOUT_ERROR',
                message: `Quá trình tạo ${count} bài tập mất quá nhiều thời gian (>${estimatedTime} phút).`,
                details: `Hãy thử giảm số lượng câu hỏi hoặc thử lại sau. OpenAI đang xử lý chậm.`,
                suggestion: count > 5 ? 'Thử tạo ít câu hơn (3-5 câu) để nhanh hơn' : 'Thử lại sau vài phút'
            };
        }
        handleApiError(error, 'Không thể tạo bài tập.');
    }
};

// Original single exercise generation (backward compatibility)
const generateExercise = async (topic, difficulty = 'medium', token) => {
    try {
        // Use batch API for consistency
        const batchResult = await generateExerciseBatch(topic, difficulty, 1, token);

        if (batchResult.exercises && batchResult.exercises.length > 0) {
            return batchResult.exercises[0];
        } else {
            throw new Error('Không thể tạo bài tập');
        }
    } catch (error) {
        console.error('Error generating single exercise:', error);
        throw error;
    }
};

// 🚀 Get user statistics
const getStats = async (token) => {
    try {
        const config = getAuthConfig(token);
        const response = await axios.get(`${API_URL}/exercises/stats`, config);
        return response.data;
    } catch (error) {
        handleApiError(error, 'Không thể lấy thống kê.');
    }
};

// 🚀 Get exercise history
const getHistory = async (limit = 10, token) => {
    try {
        const config = getAuthConfig(token);
        const response = await axios.get(`${API_URL}/exercises/history?limit=${limit}`, config);
        return response.data;
    } catch (error) {
        handleApiError(error, 'Không thể lấy lịch sử.');
    }
};

// 🚀 Submit exercise results (Enhanced)
const submitResults = async (exerciseId, userAnswer, timeSpent, token) => {
    try {
        const config = getAuthConfig(token);
        const response = await axios.post(`${API_URL}/exercises/${exerciseId}/submit`,
            {
                userAnswer,
                timeSpent,
                timestamp: new Date().toISOString() // Add timestamp
            },
            config
        );
        return response.data;
    } catch (error) {
        handleApiError(error, 'Không thể gửi kết quả.');
    }
};

// 🚀 NEW: Get available topics and difficulties
const getTopicsAndDifficulties = () => {
    return {
        topics: [
            'Present Perfect',
            'Past Simple',
            'Conditional Sentences',
            'Modal Verbs',
            'Passive Voice',
            'Future Tenses',
            'Gerunds and Infinitives',
            'Relative Clauses',
            'Reported Speech',
            'Phrasal Verbs'
        ],
        difficulties: [
            { value: 'easy', label: 'Dễ (A1-A2)', description: 'Ngữ pháp cơ bản' },
            { value: 'medium', label: 'Trung bình (B1-B2)', description: 'Ngữ pháp trung cấp' },
            { value: 'hard', label: 'Khó (C1-C2)', description: 'Ngữ pháp nâng cao' }
        ]
    };
};

const validateBatchRequest = (topic, difficulty, count) => {
    const difficulties = ['easy', 'medium', 'hard'];
    const errors = [];

    // ✅ RELAXED topic validation - accept any grammar topic
    if (!topic || topic.trim().length === 0) {
        errors.push('Vui lòng nhập chủ đề ngữ pháp');
    } else if (topic.trim().length < 2) {
        errors.push('Chủ đề phải có ít nhất 2 ký tự');
    }
    // ❌ REMOVED: strict topic list check

    if (!difficulty || !difficulties.includes(difficulty)) {
        errors.push('Độ khó không hợp lệ');
    }

    if (!count || count < 1 || count > 20) {
        errors.push('Số lượng bài tập phải từ 1-20');
    }

    return {
        isValid: errors.length === 0,
        errors
    };
};

// 🚀 NEW: Get exercise by ID
const getExerciseById = async (exerciseId, token) => {
    try {
        const config = getAuthConfig(token);
        const response = await axios.get(`${API_URL}/exercises/${exerciseId}`, config);
        return response.data;
    } catch (error) {
        handleApiError(error, 'Không thể lấy bài tập.');
    }
};

// 🚀 NEW: Progress tracking
const updateProgress = async (exerciseId, progress, token) => {
    try {
        const config = getAuthConfig(token);
        const response = await axios.patch(`${API_URL}/exercises/${exerciseId}/progress`,
            { progress },
            config
        );
        return response.data;
    } catch (error) {
        // Don't throw error for progress updates to avoid disrupting user experience
        // But still handle 401 errors
        if (error.response && error.response.status === 401) {
            logoutUser();
        }
        console.error('Error updating progress:', error);
        return null;
    }
};

// 🚀 Utility function for handling loading states
const withLoadingState = async (asyncFunction, setLoading) => {
    try {
        setLoading(true);
        const result = await asyncFunction();
        return result;
    } finally {
        setLoading(false);
    }
};

const exerciseService = {
    // Core functions
    generateExercise,           // Single exercise (uses batch internally)
    generateExerciseBatch,      // 🚀 Multiple exercises (MAIN METHOD)

    // User data
    getStats,                   // 🚀 User statistics
    getHistory,                 // 🚀 Exercise history
    submitResults,              // 🚀 Submit results

    // 🚀 New utility functions
    getTopicsAndDifficulties,   // Available options
    validateBatchRequest,       // Validation
    getExerciseById,           // Get specific exercise
    updateProgress,            // Progress tracking
    withLoadingState           // Loading helper
};

export default exerciseService;